'use client'

import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { LogIn, User } from "lucide-react"
import { useRouter } from "next/navigation"
import { useAuth } from "@/lib/auth-provider"
import { useIsMobile } from "@/hooks/use-mobile"
import { useLanguage } from "@/lib/i18n/language-provider"

interface AuthButtonProps {
  variant?: "default" | "outline" | "ghost"
  size?: "default" | "sm" | "lg"
  showText?: boolean // Permet de masquer le texte sur mobile
  avatarOnly?: boolean // Si true, affiche seulement l'avatar en mode rond
  className?: string
}

export function AuthButton({
  variant = "outline",
  size = "default",
  showText = true,
  avatarOnly = false,
  className = ""
}: AuthButtonProps) {
  const { user, profile, loading } = useAuth()
  const router = useRouter()
  const isMobile = useIsMobile()
  const { t } = useLanguage()

  // Pendant le chargement, afficher un skeleton ou rien
  if (loading) {
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        className={className}
      >
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </Button>
    )
  }

  // Si l'utilisateur est connecté
  if (user) {
    const initial = (profile?.display_name || user.email || 'U')[0].toUpperCase()
    const avatarUrl = profile?.avatar_url || null
    
    // Mode avatar uniquement (bouton rond)
    if (avatarOnly) {
      return (
        <Button
          variant={variant}
          size="icon"
          onClick={() => router.push('/account')}
          className={`${className} h-9 w-9 rounded-full border-primary/50 hover:border-primary p-0`}
        >
          <Avatar className="h-8 w-8">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={profile?.display_name || 'User'} />}
            <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
              {initial}
            </AvatarFallback>
          </Avatar>
        </Button>
      )
    }
    
    // Mode normal avec texte
    return (
      <Button
        variant={variant}
        size={size}
        onClick={() => router.push('/account')}
        className={`${className} border-primary/50 hover:border-primary`}
      >
        {/* Avatar */}
        <Avatar className="h-6 w-6 mr-2">
          {avatarUrl && <AvatarImage src={avatarUrl} alt={profile?.display_name || 'User'} />}
          <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
            {initial}
          </AvatarFallback>
        </Avatar>
        
        {/* Texte (masqué sur mobile si showText=false) */}
        {(showText || !isMobile) && (
          <span>{t("authButton.myAccount")}</span>
        )}
      </Button>
    )
  }

  // Si l'utilisateur n'est pas connecté
  return (
    <Button
      variant={variant}
      size={size}
      onClick={() => router.push('/login')}
      className={className}
    >
      <LogIn className="h-4 w-4 mr-2" />
      {(showText || !isMobile) && (
        <span>{t("authButton.signIn")}</span>
      )}
    </Button>
  )
}

